/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { SessionSibling } from "@app/server/trpc/routers/session";
import { RestaurantSwitcherImage } from "@app/vue/components/LayoutComponents/Navbar/RestaurantSwitcherImage";

export type RestaurantPickerModalProps = {
  shops: SessionSibling[];
  switchingUserId: number | null;
  onPick: (sibling: SessionSibling) => void;
  onCancel: () => void;
};

// Shown right after an identity with more than one linked restaurant signs in,
// so the manager chooses which shop to open in POS instead of landing on the
// remembered default.
export const RestaurantPickerModal =
  defineComponent<RestaurantPickerModalProps>({
    name: "RestaurantPickerModal",
    props: ["shops", "switchingUserId", "onPick", "onCancel"],
    setup(props) {
      const { t } = useLocalization();
      const orderedShops = computed(() =>
        [...props.shops].sort(
          (shopA, shopB) => Number(shopB.is_current) - Number(shopA.is_current),
        ),
      );

      return () => {
        const busy = props.switchingUserId !== null;

        return (
          <div class="modal modal-open">
            <div class="modal-box max-w-md rounded-2xl border border-base-content/10 p-0">
              <div class="px-6 pt-6 pb-4 text-center">
                <span class="material-symbols-outlined mb-2 text-4xl text-accent">
                  storefront
                </span>
                <h3 class="text-2xl font-semibold text-accent">
                  {t("auth.login.picker_title")}
                </h3>
                <p class="mx-auto mt-1 max-w-xs text-sm text-base-content/60">
                  {t("auth.login.picker_subtitle")}
                </p>
              </div>

              <ul class="flex max-h-[55vh] flex-col gap-2 overflow-y-auto px-4 pb-6">
                {orderedShops.value.map((shop) => {
                  const isSwitching = props.switchingUserId === shop.user_id;
                  return (
                    <li key={shop.user_id}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => props.onPick(shop)}
                        class={[
                          "group flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors duration-150",
                          isSwitching
                            ? "border-accent bg-accent/5"
                            : "border-base-content/10 hover:border-accent/50 hover:bg-accent/5",
                          busy && !isSwitching && "opacity-50",
                        ]}
                      >
                        {isSwitching ? (
                          <span class="flex h-9 w-9 shrink-0 items-center justify-center">
                            <span class="loading loading-spinner loading-sm text-accent" />
                          </span>
                        ) : (
                          <span class="[&_img]:h-9 [&_img]:w-9 [&_span]:h-9 [&_span]:w-9 [&_span]:text-base">
                            <RestaurantSwitcherImage
                              name={shop.restaurant_name}
                              url={shop.image_url}
                            />
                          </span>
                        )}

                        <span class="min-w-0 flex-1">
                          <span class="block truncate font-semibold text-base-content group-hover:text-accent">
                            {shop.restaurant_name}
                          </span>
                          {shop.restaurant_address && (
                            <span class="block truncate text-xs text-base-content/50">
                              {shop.restaurant_address}
                            </span>
                          )}
                        </span>

                        {shop.is_current && (
                          <span class="badge badge-outline badge-sm shrink-0 border-accent/40 text-accent">
                            {t("auth.login.picker_current")}
                          </span>
                        )}

                        <span class="material-symbols-outlined shrink-0 text-base-content/30 transition-colors group-hover:text-accent">
                          chevron_right
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div class="border-t border-base-content/10 px-4 py-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => props.onCancel()}
                  class="btn btn-ghost btn-sm w-full text-base-content/60 hover:text-accent"
                >
                  {t("general.words.cancel")}
                </button>
              </div>
            </div>
            <button
              type="button"
              aria-label={t("general.words.cancel")}
              disabled={busy}
              onClick={() => props.onCancel()}
              class="modal-backdrop bg-base-content/10"
            />
          </div>
        );
      };
    },
  });
